"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Truck, Music, Building2 } from "lucide-react";

const SCENARIOS = [
  {
    icon: Truck,
    title: { en: "Food truck at a festival", es: "Food truck en festival" },
    sub: { en: "200 trucks fighting for one weak 4G tower. Yours keeps ringing cash.", es: "200 trucks peleando por una torre 4G débil. El tuyo sigue cobrando en efectivo." },
  },
  {
    icon: Music,
    title: { en: "Concert venue, packed", es: "Venue lleno de concierto" },
    sub: { en: "5,000 phones killed the venue WiFi at 9:47 PM. Your bar keeps taking cash.", es: "5,000 phones mataron el WiFi a las 9:47 PM. Tu barra sigue cobrando en efectivo." },
  },
  {
    icon: Building2,
    title: { en: "Strip mall outage", es: "Apagón en strip mall" },
    sub: { en: "A street crew nicked the fiber down the block. You still ring cash at lunch.", es: "Un crew de la calle cortó la fibra en la cuadra. Tú sigues cobrando el almuerzo en efectivo." },
  },
];

export default function OfflineFestivalScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Real-world scenarios", "Escenarios reales")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Built for the ", "Hecho para los ")}
            <span className="text-redy-charcoal/30">{t("nights it matters.", "días que importa.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Cards need a connection. Cash doesn't. Redy keeps the cash line moving in the spots where you actually make money — festivals, events, packed venues, sketchy strip malls.",
              "La tarjeta necesita conexión. El efectivo no. Redy mantiene avanzando la fila de efectivo en los lugares donde realmente ganas dinero — festivales, eventos, venues llenos, strip malls sketchy."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[1080px] mx-auto">
          {SCENARIOS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title.en}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="bg-[#FAFAF8] border border-black/5 rounded-[20px] p-6"
              >
                <div
                  className="w-11 h-11 rounded-[12px] flex items-center justify-center mb-5"
                  style={{ backgroundColor: "rgba(244,114,43,0.12)" }}
                >
                  <Icon className="w-5 h-5" style={{ color: "#F4722B" }} />
                </div>
                <div className="text-[18px] font-black text-redy-charcoal mb-3 tracking-tight">
                  {t(s.title.en, s.title.es)}
                </div>
                <div className="text-[13px] font-medium text-redy-secondary leading-relaxed">
                  {t(s.sub.en, s.sub.es)}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
